import React, { useState, useCallback } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { getBrandValue } from '~/shared/utils/brandNameHelper';
import {
  RadioButtonGroup,
  RadioButton,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: bold;
  text-align: left;
  padding-bottom: 16px;
  line-height: 1.5;
  color: ${({ theme, isError, isSelected }) =>
    isError && !isSelected ? theme.colors.error : theme.colors.palette.black};
  display: block;
  font-size: 1em;
  margin-top: 0.67em;
  margin-left: 0;
  margin-right: 0;
  font-weight: 550;

  ${({ isRequired, theme }) =>
    isRequired &&
    css`
      ::after {
        content: '*';
        color: ${theme.colors.textDefault};
      }
    `}
`;

const OptionalText = styled.span`
  color: ${({ theme }) => theme.colors.textDefault};
  font-weight: lighter;
  margin-left: 7px;
`;

const RadioButtonOuter = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  padding-bottom: 5px;
  padding-top: 5px;
  background: transparent;
`;

const inputStyle = css`
  border: 4px solid ${({ theme }) => theme.colors.borderLightAlternate};
`;

const inputCheckedStyle = css`
  border: 4px solid ${({ theme }) => theme.colors.textEmphasized};
  background: ${({ theme }) => theme.colors.textEmphasized};
`;

const RadioButtonLabel = styled.div`
  text-align: left;
  width: 100%;
  margin-left: ${({ theme }) => theme.sizes.xxxs}px;
  ${({ theme }) => theme.reviewStyles.radioLabelStyle};
`;

const StyledRadioButtonGroup = styled(RadioButtonGroup)`
  ${({ layout }) =>
    layout === 'horizontal' &&
    css`
      display: flex;
      flex-direction: row;
      gap: ${({ theme }) => theme.sizes.xxxs}px;
    `}
`;

const DynamicFormattedMessage = FormattedMessage;

const ReviewOptionRadio = ({
  className,
  isError,
  label,
  onChange,
  option,
  rbKey,
  title,
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState({
    id: 0,
    selectedOption: 0,
    reviewTemplateFieldAnswerId: null,
    error: true,
  });

  const handleChange = useCallback(
    (answerId) => {
      // We need to determine what object to send up to the parent. This rating should pertain to the option's sort value
      const answerObject = option.answers.find(
        (answer) => answer.reviewTemplateFieldAnswerId === answerId
      );
      const nextState = {
        ...selectedAnswer,
        selectedOption: answerId,
        reviewTemplateFieldAnswerId: answerObject.reviewTemplateFieldAnswerId, // We pass this up to the parent to handle submit logic
        id: answerObject.reviewTemplateFieldId,
        error: false,
      };
      setSelectedAnswer({ ...nextState });
      onChange(nextState);
    },
    [option.answers, selectedAnswer, onChange]
  );
  // Sanitize the options coming from the API...
  const radioOptions = option.answers.map((option) => {
    const id = option.reviewTemplateFieldAnswerId;
    const labelDisplay = `${option.label}`;
    const labelFormat =
      labelDisplay.charAt(0).toUpperCase() + labelDisplay.slice(1);

    return {
      value: id,
      label: labelFormat,
      sort: option.sort,
    };
  });

  const radioOptionsLayout = getBrandValue({
    savage: 'vertical',
    default:
      radioOptions && radioOptions.length > 2 ? 'vertical' : 'horizontal',
  });

  return (
    <Wrapper className={className}>
      <Title
        isError={isError}
        isSelected={selectedAnswer.selectedOption}
        isRequired={option.required}
      >
        <DynamicFormattedMessage id={rbKey} defaultMessage={title} />
        {option.required ? null : (
          <OptionalText>
            <FormattedMessage
              defaultMessage="(Optional)"
              id="site_account_reviews.reviews_requiredoptional"
            />
          </OptionalText>
        )}
      </Title>
      <StyledRadioButtonGroup layout={radioOptionsLayout}>
        {radioOptions.map((option) => {
          return (
            <RadioButtonOuter key={option.value}>
              <RadioButton
                value={selectedAnswer.selectedOption}
                label={option.label}
                onClick={() => handleChange(option.value)}
                inputStyle={inputStyle}
                inputCheckedStyle={inputCheckedStyle}
                checked={selectedAnswer.selectedOption === option.value}
              />
              <RadioButtonLabel
                isSelected={selectedAnswer.selectedOption === option.value}
              >
                <DynamicFormattedMessage
                  id={`site_account_reviews.review_${label}_option_${option.sort}`}
                  defaultMessage={option.label}
                />
              </RadioButtonLabel>
            </RadioButtonOuter>
          );
        })}
      </StyledRadioButtonGroup>
    </Wrapper>
  );
};

ReviewOptionRadio.propTypes = {
  className: PropTypes.string,
  isError: PropTypes.bool,
  label: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  option: PropTypes.object.isRequired,
  rbKey: PropTypes.string,
  title: PropTypes.string.isRequired,
};

export default ReviewOptionRadio;
